/**
 * // Class Obj: サイト内の画像やiframeの遅延読み込み
 */
export class GraphicsLazyController
{
  private static readonly LOADED_CLASS_NAME: string = 'is_loaded';
  private readonly mLazyTargets: Array<LazyTarget>;
  private readonly mDefaultOptions: Options;

  /**
   * // Constructor: サイト内の背景画像やiframeを監視の対象とするオプションを受け取る
   *
   * @param lazy_targets 遅延読み込みの対象セレクタとオプションの配列
   */
  constructor(lazy_targets: Array<LazyTarget>)
  {
    this.mLazyTargets = lazy_targets; // 遅延読み込み対象を保存

    // 遅延読み込みオプション
    this.mDefaultOptions = {
      root: null, // ビューポートを基準にする
      rootMargin: '0px', // 〇〇px手前で読み込み開始
      threshold: 0, // 0に設定して、任意の交差量でトリガーされるようにする
    };
  }

  /**
   * // Method: 遅延読み込み対象の要素を監視
   */
  public loadGraphics(): void
  {
    const site_body: HTMLElement | null = document.body;

    if (!site_body) return;

    // 遅延読み込み対象から、それぞれのプロパティを抽出
    this.mLazyTargets.forEach(({ selector, options = {} }: LazyTarget) =>
    {
      // セレクタをinitObserverメソッドに渡す
      this.initObserver(site_body, selector, options);
    });
  }

  /**
   * // Method: IntersectionObserverを初期化
   *
   * @param site_body 監視対象を検索する基準要素
   * @param selector 監視対象のCSSセレクタ
   * @param custom_options IntersectionObserverのカスタムオプション設定
   */
  private initObserver(site_body: HTMLElement, selector: string, custom_options: Options = {}): void
  {
    // デフォルトオプションとカスタムオプションを統合（マージ）する
    const options: Options = { ...this.mDefaultOptions, ...custom_options };

    // 監視対象の要素をCSSセレクタで取得
    const elements: Array<HTMLElement> = Array.from(
      site_body.querySelectorAll<HTMLElement>(selector),
    );

    if (elements.length === 0) return;

    // IntersectionObserverが使えない環境では、対象を即時読み込み状態にする
    if (!('IntersectionObserver' in window))
    {
      elements.forEach((element: HTMLElement) =>
      {
        this.loadTarget(element);
      });

      return;
    }

    // オブサーバーのコールバック関数をバインドして設定
    const iIntersectionObserver = new IntersectionObserver(
      this.onIntersection.bind(this),
      options,
    );

    // 監視対象の要素を一つずつオブサーバーに登録
    elements.forEach((element: HTMLElement) =>
    {
      iIntersectionObserver.observe(element);
    });
  }

  /**
   * // Method: 指定されたエントリーが交差したときに特定の処理を行う
   *
   * @param entries 交差を監視する要素の配列（observer固有のタイプ）
   * @param observer 交差を監視するためのIntersectionObserverインスタンス
   */
  private onIntersection(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void
  {
    // 各エントリーについて処理を行う
    entries.forEach((entry: IntersectionObserverEntry) =>
    {
      // エントリーが交差状態でない場合は処理しない
      if (!entry.isIntersecting) return;

      const lazy_graphic: Element = entry.target; // 遅延読み込み対象の要素を取得

      this.loadTarget(lazy_graphic);

      // オブサーバーから解除する
      observer.unobserve(lazy_graphic);
    });
  }

  /**
   * // Method: 遅延読み込み対象の読み込み処理
   *
   * @param lazy_graphic 遅延読み込み対象の要素
   */
  private loadTarget(lazy_graphic: Element): void
  {
    // iframe要素の場合、src属性をdata-src属性の値に置き換える
    if (lazy_graphic instanceof HTMLIFrameElement)
    {
      // もしdata-src属性が存在すればその値を取得する
      const lazy_src: string | null = lazy_graphic.getAttribute('data-src');

      if (lazy_src)
      {
        lazy_graphic.src = lazy_src;
      }
    }

    // 読み込み完了クラスを追加
    lazy_graphic.classList.add(GraphicsLazyController.LOADED_CLASS_NAME);
  }
}

//////////////////////////////////////////////////
/// AREA: types
//////////////////////////////////////////////////
type Options = {
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | Array<number>;
};

type LazyTarget = {
  selector: string;
  options?: Options;
};

//////////////////////////////////////////////////
///  AREA: END
//////////////////////////////////////////////////